import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getSessionStorage } from "@jti/rest-api";
import { applyCampaign, loyalty, removeCampaign, useDispatch, useSelector } from "@jti/store";
import { useAemModel } from "../../contexts/AemModelContext";
import { useLoyaltyProductPoints } from "../../hooks/useLoyaltyProductPoints";
import getSelectedVoucher from "../../utils/getSelectedVoucher";
export var LOYALTY_CHOICE;
(function (LOYALTY_CHOICE) {
    LOYALTY_CHOICE[LOYALTY_CHOICE["COLLECT"] = 0] = "COLLECT";
    LOYALTY_CHOICE[LOYALTY_CHOICE["REDEEM"] = 1] = "REDEEM";
})(LOYALTY_CHOICE || (LOYALTY_CHOICE = {}));
const getMinVoucherCost = (vouchers) => vouchers?.find(({ is_eligible }) => is_eligible)?.points || 0;
const findVoucher = (vouchers, cartSubTotal) => vouchers?.find((voucher) => voucher.price >= cartSubTotal)?.price || cartSubTotal | 0;
const clearSelection = (dispatch, cartId) => {
    if (!cartId) {
        return;
    }
    dispatch(removeCampaign(cartId));
};
const setLoyaltyCartPoints = (cartItems, userSelection, cartPoints) => {
    if (cartItems?.length) {
        const loyaltyCartPoints = userSelection.loyaltyChoice === LOYALTY_CHOICE.REDEEM ? 0 : cartPoints;
        getSessionStorage().set("loyaltyCartPoints", loyaltyCartPoints);
    }
};
const setCampaignErrors = (campaignErrors) => {
    if (campaignErrors.length < 1) {
        campaignErrors.forEach((error) => {
            toast.error(error.message);
        });
    }
};
const handleVoucherErrors = (dispatch, selectedVoucher, userPoints, highestAvailableVoucher, cartId) => {
    if (selectedVoucher && (selectedVoucher.points > userPoints || selectedVoucher.price > highestAvailableVoucher)) {
        dispatch(loyalty.actions.setLoyaltyError(true));
        clearSelection(dispatch, cartId);
    }
};
const clearLoyaltyStorage = (cartItems) => {
    if (cartItems?.length === 0) {
        getSessionStorage().delete("loyaltyChoice");
    }
};
const updateUserSelection = (choice, cartId) => {
    getSessionStorage().set("loyaltyChoice", {
        loyaltyChoice: choice,
        cartId,
    });
};
export const useLoyalty = () => {
    const [isQueryCampaign, setIsQueryCampaign] = useState(false);
    const [hasCampaignSelected, setHasCampaignSelected] = useState(false);
    const userSelection = getSessionStorage().get("loyaltyChoice", {
        loyaltyChoice: 0,
        cartId: "",
    });
    const { loyaltyChoice, points: userPoints, vouchers, isLoyaltyError, loyaltyCampaignId, campaignErrors = [], } = useSelector((store) => store.loyalty);
    const dispatch = useDispatch();
    const cartId = useSelector((store) => store.cart.id);
    const aemModel = useAemModel();
    const cartSubTotal = useSelector((store) => store.cart.prices.data?.subtotal?.value ?? 0);
    const cartItems = useSelector((store) => store.cart.items);
    const itemsPending = useSelector((store) => store.cart.itemsPending);
    const cartPoints = useLoyaltyProductPoints(cartItems);
    const highestAvailableVoucher = findVoucher(vouchers, cartSubTotal);
    const availableVouchers = vouchers.filter((voucher) => voucher.is_eligible);
    const maxVoucherValue = availableVouchers[availableVouchers.length - 1]?.price ?? 0;
    const minVoucherCost = getMinVoucherCost(vouchers);
    const redemptionTabSymbol = aemModel.loyalty?.redemptionTabSymbol || "%";
    const setCollect = () => {
        dispatch(loyalty.actions.setLoyaltyChoice(LOYALTY_CHOICE.COLLECT));
        updateUserSelection(LOYALTY_CHOICE.COLLECT, cartId);
        if (!cartId) {
            return;
        }
        dispatch(removeCampaign(cartId));
    };
    const setRedeem = () => {
        dispatch(loyalty.actions.setLoyaltyChoice(LOYALTY_CHOICE.REDEEM));
        updateUserSelection(LOYALTY_CHOICE.REDEEM, cartId);
    };
    const handleApplyCampaign = async (campaginUuid) => {
        if (!cartId) {
            return;
        }
        try {
            await dispatch(applyCampaign({ cartId, campaign_uuid: campaginUuid }));
        }
        catch (error) {
            dispatch(loyalty.actions.setLoyaltyError(true));
        }
    };
    const handleCampaignChange = async (input) => {
        setIsQueryCampaign(true);
        dispatch(loyalty.actions.setLoyaltyError(false));
        const campaignId = typeof input === "string" ? input : input.target.value;
        await handleApplyCampaign(campaignId);
        setIsQueryCampaign(false);
    };
    useEffect(() => {
        const loyaltyChoice = cartId && userSelection.cartId === cartId && availableVouchers.length
            ? userSelection.loyaltyChoice
            : LOYALTY_CHOICE.COLLECT;
        dispatch(loyalty.actions.setLoyaltyChoice(loyaltyChoice));
    }, [availableVouchers?.length, cartId, dispatch, userSelection.cartId, userSelection.loyaltyChoice, cartPoints]);
    useEffect(() => {
        setLoyaltyCartPoints(cartItems, userSelection, cartPoints);
    }, [cartPoints, userSelection.loyaltyChoice]);
    useEffect(() => {
        const selectedVoucher = getSelectedVoucher(vouchers, loyaltyCampaignId);
        const selectedCampaign = (!selectedVoucher && userSelection.loyaltyChoice === LOYALTY_CHOICE.REDEEM) || true;
        setHasCampaignSelected(selectedCampaign);
        handleVoucherErrors(dispatch, selectedVoucher, userPoints, highestAvailableVoucher, cartId);
        clearLoyaltyStorage(cartItems);
    }, [cartItems?.length, cartSubTotal, loyaltyCampaignId, userPoints, vouchers?.length, userSelection.loyaltyChoice]);
    useEffect(() => {
        setCampaignErrors(campaignErrors);
    }, [campaignErrors]);
    useEffect(() => {
        if (!itemsPending && loyaltyCampaignId && loyaltyChoice === LOYALTY_CHOICE.COLLECT) {
            dispatch(loyalty.actions.setLoyaltyChoice(LOYALTY_CHOICE.REDEEM));
            updateUserSelection(LOYALTY_CHOICE.REDEEM, cartId);
        }
    }, [itemsPending, loyaltyCampaignId]);
    return {
        cartPoints,
        loyaltyChoice,
        userPoints,
        vouchers,
        isQueryCampaign,
        loyaltyCampaignId,
        isLoyaltyError,
        maxVoucherValue,
        minVoucherCost,
        setCollect,
        setRedeem,
        handleCampaignChange,
        highestAvailableVoucher,
        hasCampaignSelected,
        redemptionTabSymbol,
    };
};
//# sourceMappingURL=useLoyalty.js.map